// 商品庫存的儲位統計與碎片偵測。
//
// 此檔把原本散在 `ProductInventorySummary` 裡的計算抽出來，讓「庫存總覽」與
// 新的「儲位分佈」視角共用同一份判定邏輯（DRY）。輸入是 `get_records` 回傳、
// 每筆內嵌 `shelf_inventories[]` 的 inventory 陣列。

// 碎片門檻：同一筆 inventory 內，storage 儲位數量 < 第二大儲位的 50% 視為碎片。
const FRAGMENT_RATIO = 0.5;

export interface ShelfInventoryLike {
  id?: string;
  shelf_id?: string;
  shelf_location?: string | null;
  shelf_type_name?: string | null;
  shelf_role?: string | null;
  quantity: number;
}

export interface InventoryLike {
  id: string;
  product_sku?: string | null;
  inventory_type?: string | null;
  batch?: string | null;
  expiration_date?: string | null;
  shelf_inventories?: ShelfInventoryLike[];
}

export interface FragmentDetail {
  shelf_location?: string | null;
  shelf_type_name?: string | null;
  quantity: number;
  inventory_total: number;
  benchmark_quantity: number;
  percentage: number;
  product_sku?: string | null;
  batch?: string | null;
  expiration_date?: string | null;
  inventory_type?: string | null;
}

export interface ShelfStats {
  totalShelves: number;
  concentration: number | null;
  fragments: FragmentDetail[];
  fragmentKeys: Set<string>;
}

// 只計算 storage 儲位（排除 staging / carrier）。
export const isStorageShelf = (si: ShelfInventoryLike): boolean =>
  !si.shelf_role || si.shelf_role === 'storage';

// 攤平列 / 碎片集合共用的 key：`${inventory_id}-${shelf_inventory_id}`。
export const shelfEntryKey = (inventoryId: string, shelfInventoryId: string): string =>
  `${inventoryId}-${shelfInventoryId}`;

/**
 * 從 inventories（每筆內嵌 shelf_inventories）計算儲位統計與碎片。
 *
 * - `totalShelves`：distinct storage 儲位數
 * - `concentration`：非碎片儲位 / 參與計算儲位（%）；完全沒有 storage 儲位時為 null
 * - `fragments`：碎片明細（給「庫存總覽」的碎片 modal 用）
 * - `fragmentKeys`：碎片的 `${inventory_id}-${shelf_inventory_id}` 集合（給「儲位分佈」標 tag 用）
 */
export function computeShelfStats(inventories: InventoryLike[] = []): ShelfStats {
  const allShelfIds = new Set<string>();
  const fragments: FragmentDetail[] = [];
  const fragmentKeys = new Set<string>();
  let totalShelvesForConc = 0; // 有參與計算的儲位總數
  let totalNonFragments = 0; // 非碎片儲位數

  inventories.forEach((inv) => {
    const storageShelves = (inv.shelf_inventories || []).filter(isStorageShelf);
    storageShelves.forEach((si) => {
      const sid = si.shelf_id || si.id;
      if (sid) allShelfIds.add(sid);
    });
    if (storageShelves.length === 0) return;

    // ≤2 個儲位：不比較碎片，全部算非碎片。
    if (storageShelves.length <= 2) {
      totalShelvesForConc += storageShelves.length;
      totalNonFragments += storageShelves.length;
      return;
    }

    const quantities = storageShelves.map((si) => si.quantity);
    const sorted = [...quantities].sort((a, b) => b - a);
    const benchmark = sorted[1]; // 第二大
    const total = quantities.reduce((sum, q) => sum + q, 0);
    let invFragments = 0;

    storageShelves.forEach((si) => {
      if (si.quantity < benchmark * FRAGMENT_RATIO) {
        invFragments += 1;
        if (inv.id && si.id) fragmentKeys.add(shelfEntryKey(inv.id, si.id));
        fragments.push({
          shelf_location: si.shelf_location,
          shelf_type_name: si.shelf_type_name,
          quantity: si.quantity,
          inventory_total: total,
          benchmark_quantity: benchmark,
          percentage: Math.round((si.quantity / benchmark) * 100),
          product_sku: inv.product_sku,
          batch: inv.batch,
          expiration_date: inv.expiration_date,
          inventory_type: inv.inventory_type,
        });
      }
    });

    totalShelvesForConc += storageShelves.length;
    totalNonFragments += storageShelves.length - invFragments;
  });

  const shelfCount = allShelfIds.size;
  let concentration: number | null;
  if (shelfCount === 0) {
    concentration = null;
  } else if (shelfCount <= 1) {
    concentration = 100;
  } else {
    concentration =
      totalShelvesForConc > 0
        ? Math.round((totalNonFragments / totalShelvesForConc) * 100)
        : 100;
  }

  return { totalShelves: shelfCount, concentration, fragments, fragmentKeys };
}
